/**
 * Utility functions for job posting validation and data transformation
 */

import {
  VALIDATION_PATTERNS,
  SKILL_CONSTRAINTS,
  EXPERIENCE_LEVELS
} from './constants';

/**
 * Format date string from ISO format to YYYY-MM-DD
 * @param {string} dateString - ISO date string or date input
 * @returns {string} - Formatted date or empty string
 */
export const formatDateForAPI = (dateString) => {
  if (!dateString) return null;
  return dateString.split('T')[0];
};

/**
 * Format date for input field (same as formatDateForAPI)
 * @param {string} dateString
 * @returns {string}
 */
export const formatDateForInput = (dateString) => {
  if (!dateString) return '';
  return dateString.split('T')[0];
};

/**
 * Sanitize responsibilities text coming from AI or user input.
 * - If input is a JSON array string, parse and join with newlines
 * - Remove common JSON punctuation/brackets/quotes
 * - Convert bullets and sentence endings into newline-separated lines
 * @param {string} input
 * @returns {string}
 */
export const sanitizeResponsibilities = (input) => {
  if (!input) return '';
  let text = String(input).trim();

  // Try parsing if it looks like JSON
  if ((text.startsWith('[') && text.endsWith(']')) || text.startsWith('{')) {
    try {
      const parsed = JSON.parse(text);
      if (Array.isArray(parsed)) {
        return parsed
          .map(s => String(s || '').replace(/^[\u2022\-\*\s]+/, '').trim())
          .filter(Boolean)
          .join('\n');
      }
      if (typeof parsed === 'string') {
        text = parsed.trim();
      }
    } catch (e) {
      // fallthrough and try to clean heuristically
    }
  }

  // Remove stray brackets/quotes
  text = text.replace(/^[\[\]{}]+|[\[\]{}]+$/g, '').trim();

  // If it's a quoted, comma-separated list like "a","b","c"
  if (text.includes('\",\"')) {
    const parts = text.split('\",\"').map(s => s.replace(/^\"|\"$/g, '').trim());
    return parts.map(p => p.replace(/^[\u2022\-\*\s]+/, '').replace(/,+$/,'').trim()).filter(Boolean).join('\n');
  }

  // Replace common bullet characters with newlines
  text = text.replace(/[\u2022\u2023\u25E6\u2043\u2219]/g, '\n');
  // Replace hyphen bullets at line starts with newline
  text = text.replace(/(^|\n)\s*[-–—]\s*/g, '\n');

  // Normalize newlines then split
  text = text.replace(/\r/g, '\n');
  const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
  if (lines.length > 1) {
    return lines.map(l => l.replace(/^\"|\"$/g, '').replace(/,+$/,'').trim()).filter(Boolean).join('\n');
  }

  // Otherwise split sentences into lines by punctuation followed by space + capital/digit
  const sentenceSeparated = text.replace(/([\.\?!])\s+(?=[A-Z0-9])/g, '$1\n');
  const cleaned = sentenceSeparated.replace(/^\"|\"$/g, '').replace(/,+$/,'').trim();
  return cleaned;
};

/**
 * Sanitize and validate skills array
 * Ensures each skill is valid, removes invalid ones, and enforces limits
 * @param {Array} skills - Array of skill strings
 * @returns {Array} - Sanitized skills array
 */
export const sanitizeSkills = (skills) => {
  if (!Array.isArray(skills)) return [];

  return skills
    .map(skill => {
      if (typeof skill !== 'string') return null;
      const sanitized = skill.trim();

      // Validate length
      if (
        sanitized.length < SKILL_CONSTRAINTS.MIN_LENGTH ||
        sanitized.length > SKILL_CONSTRAINTS.MAX_LENGTH
      ) {
        return null;
      }

      // Validate characters
      if (!VALIDATION_PATTERNS.SKILL.test(sanitized)) {
        return null;
      }

      return sanitized;
    })
    .filter(skill => skill !== null)
    .slice(0, SKILL_CONSTRAINTS.MAX_SKILLS);
};

/**
 * Validate a single step of the job posting form
 * @param {number} step - Step number (1-3)
 * @param {Object} jobData - Current job data
 * @returns {Object} - Errors object (empty if valid)
 */
export const validateJobStep = (step, jobData) => {
  const errors = {};

  const isFieldEmpty = (value) => {
    return !value || (typeof value === 'string' && !value.trim());
  };

  switch (step) {
    case 1:
      // Validate Job Details
      if (isFieldEmpty(jobData.title)) {
        errors.title = 'Job title is required';
      }
      if (isFieldEmpty(jobData.description)) {
        errors.description = 'Job description is required';
      }
      if (isFieldEmpty(jobData.location)) {
        errors.location = 'Location is required';
      }
      break;

    case 2:
      // Validate Requirements
      if (isFieldEmpty(jobData.requirements)) {
        errors.requirements = 'Requirements are required';
      }
      if (isFieldEmpty(jobData.responsibilities)) {
        errors.responsibilities = 'Responsibilities are required';
      }

      // Validate skills
      if (
        !jobData.skills_required ||
        !Array.isArray(jobData.skills_required) ||
        jobData.skills_required.length === 0
      ) {
        errors.skills_required = 'At least one skill is required';
      } else if (jobData.skills_required.length > SKILL_CONSTRAINTS.MAX_SKILLS) {
        errors.skills_required = `Maximum ${SKILL_CONSTRAINTS.MAX_SKILLS} skills allowed`;
      } else {
        // Validate each skill
        const invalidSkills = jobData.skills_required.filter(
          skill =>
            !skill ||
            typeof skill !== 'string' ||
            skill.trim().length < SKILL_CONSTRAINTS.MIN_LENGTH ||
            skill.trim().length > SKILL_CONSTRAINTS.MAX_LENGTH
        );

        if (invalidSkills.length > 0) {
          errors.skills_required = `All skills must be between ${SKILL_CONSTRAINTS.MIN_LENGTH} and ${SKILL_CONSTRAINTS.MAX_LENGTH} characters`;
        } else {
          // Check for invalid characters
          const skillsWithInvalidChars = jobData.skills_required.filter(
            skill => !VALIDATION_PATTERNS.SKILL.test(skill.trim())
          );

          if (skillsWithInvalidChars.length > 0) {
            errors.skills_required =
              'Skills contain invalid characters. Please use only letters, numbers, and common punctuation.';
          }
        }
      }
      break;

    case 3:
      // Validate Compensation
      if (!jobData.salary_min) {
        errors.salary_min = 'Minimum salary is required';
      }
      if (!jobData.salary_max) {
        errors.salary_max = 'Maximum salary is required';
      }
      if (
        jobData.salary_min &&
        jobData.salary_max &&
        parseInt(jobData.salary_min) >= parseInt(jobData.salary_max)
      ) {
        errors.salary_max = 'Maximum salary must be higher than minimum';
      }
      break;
  }

  return errors;
};

/**
 * Validate all steps at once
 * @param {Object} jobData
 * @returns {Object} - Combined errors from all steps
 */
export const validateAllSteps = (jobData) => {
  let allErrors = {};
  for (let i = 1; i <= 3; i++) {
    const stepErrors = validateJobStep(i, jobData);
    allErrors = { ...allErrors, ...stepErrors };
  }
  return allErrors;
};

/**
 * Prepare job payload for API submission
 * @param {Object} jobData - Raw job data from form
 * @param {string} userId - Current user ID
 * @param {string} userCompanyName - User's company name fallback
 * @returns {Object} - API-ready payload
 */
export const prepareJobPayload = (jobData, userId, userCompanyName) => {
  return {
    title: jobData.title,
    description: jobData.description,
    requirements: jobData.requirements 
      ? jobData.requirements.split('\n').map(r => r.trim()).filter(r => r.length > 0) 
      : [],
  responsibilities: sanitizeResponsibilities(jobData.responsibilities),
    company_name: jobData.company_name || userCompanyName,
    job_type: jobData.employment_type,
    experience_level: jobData.experience_level,
    location: jobData.location,
    is_remote: jobData.remote_work === 'remote',
    budget_min: parseFloat(jobData.salary_min) || null,
    budget_max: parseFloat(jobData.salary_max) || null,
    currency: 'USD',
    skills_required: sanitizeSkills(jobData.skills_required),
    benefits: jobData.benefits,
    application_deadline: formatDateForAPI(jobData.application_deadline),
    employer_id: userId,
    ...(jobData.public_slug && { public_slug: jobData.public_slug })
  };
};

/**
 * Get display text for experience level
 * @param {string} value - Experience level value
 * @returns {string} - Display label
 */
export const getExperienceLevelLabel = (value) => {
  const level = EXPERIENCE_LEVELS.find(l => l.value === value);
  return level ? level.label : value;
};
